/**
 * Creatives whose document renders taller than the size GAM served them at.
 *
 * The T-Mobile footer creative is a third-party DCM tag served as 320x50 whose document
 * paints ~100px tall on iOS WebKit. A 50px iframe cuts it in half, so the iOS native
 * embed used to force EVERY footer/rules/post-entry iframe to `min-height: 100px`
 * (`index.css`). That held the T-Mobile creative open — and left every well-behaved
 * 320x50 creative sitting on 50px of empty frame, a visible dead band between the ad
 * and DK's tab bar.
 *
 * GAM names the line item and creative on `slotRenderEnded`, so the tall frame can be
 * granted to the creatives that need it and nobody else. `AdSlot` stamps
 * `data-tall-creative="1"` on its container when the served creative is listed here;
 * the stylesheet keys the 100px rule on that attribute.
 *
 * Both ids are checked because ad-ops may re-traffic the same tag under a new creative
 * id on the same line item, or vice versa. Sentry tags `ad.creative_id` /
 * `ad.line_item_id` on every fill (`utils/adReporting.ts`) — if the T-Mobile tag shows
 * up half-cut again, read those two off the event and add them here.
 */

/** Line items known to carry a taller-than-declared creative. */
export const TALL_CREATIVE_LINE_ITEM_IDS: ReadonlySet<number> = new Set([
  7378726280, // T-Mobile, /free-to-play/squares/game-sticky-footer (Aug 2026)
]);

/** Creatives known to render taller than declared. */
export const TALL_CREATIVE_IDS: ReadonlySet<number> = new Set([
  138572155774, // T-Mobile DCM tag, served 320x50, paints ~100px on iOS WebKit
]);

/** Attribute `AdSlot` sets on `.ad-slot-container`; the stylesheet keys the tall frame on it. */
export const TALL_CREATIVE_ATTR = 'data-tall-creative';

export interface RenderedCreativeIds {
  creativeId?: number | null;
  lineItemId?: number | null;
  sourceAgnosticCreativeId?: number | null;
  sourceAgnosticLineItemId?: number | null;
}

/** Whether the creative GAM just rendered needs the 100px frame. Empty fills never do. */
export const needsTallFrame = (ids: RenderedCreativeIds | null | undefined): boolean => {
  if (!ids) return false;
  const candidates = [ids.creativeId, ids.sourceAgnosticCreativeId].filter(
    (v): v is number => typeof v === 'number'
  );
  const lineItems = [ids.lineItemId, ids.sourceAgnosticLineItemId].filter(
    (v): v is number => typeof v === 'number'
  );
  return (
    candidates.some(id => TALL_CREATIVE_IDS.has(id)) ||
    lineItems.some(id => TALL_CREATIVE_LINE_ITEM_IDS.has(id))
  );
};
